const CUSTOM_ERROR_TYPE = 'CustomError';
const JSLOADER_ERROR_PREFIX =
  'Jsloader error (code #0): Error while loading script https://apis.google.com/js/client.js?onload=_gapiProviderCallback_';
const TRACKING_SCRIPT_FRAME = 'tracking_script.js';

type FrameLike = {
  filename?: string;
  abs_path?: string;
};

type ExceptionLike = {
  type?: string;
  value?: string;
  stacktrace?: {
    frames?: FrameLike[];
  };
};

type JsloaderEventLike = {
  exception?: {
    values?: ExceptionLike[];
  };
};

const LIPS_SOURCE_PATTERNS = ['/_next/', '/src/', 'lips.org.uk'];

// Sentry SDK / Application Insights instrumentation frames must NOT count as
// LIPS business code. An injected-loader failure whose only app-looking frames
// are telemetry plumbing is still external noise.
const INSTRUMENTATION_PATTERNS = [
  '@sentry',
  'sentry',
  'applicationinsights',
  'appinsights',
];

function framePath(frame: FrameLike): string {
  return `${frame.abs_path ?? ''} ${frame.filename ?? ''}`;
}

function isLipsSourcePath(frame: FrameLike): boolean {
  return LIPS_SOURCE_PATTERNS.some((pattern) =>
    framePath(frame).includes(pattern),
  );
}

function isInstrumentationFrame(frame: FrameLike): boolean {
  const path = framePath(frame);
  return INSTRUMENTATION_PATTERNS.some((pattern) => path.includes(pattern));
}

function isLipsBusinessFrame(frame: FrameLike): boolean {
  return isLipsSourcePath(frame) && !isInstrumentationFrame(frame);
}

export function isKnownGoogleApiInjectedLoaderError(event: unknown): boolean {
  if (event === null || typeof event !== 'object') {
    return false;
  }

  const candidate = event as JsloaderEventLike;
  const exceptions = candidate.exception?.values;

  if (
    !exceptions?.some(
      (exception) =>
        exception.type === CUSTOM_ERROR_TYPE &&
        (exception.value ?? '').startsWith(JSLOADER_ERROR_PREFIX),
    )
  ) {
    return false;
  }

  const frames = exceptions.flatMap(
    (exception) => exception.stacktrace?.frames ?? [],
  );

  if (
    !frames.some((frame) => framePath(frame).includes(TRACKING_SCRIPT_FRAME))
  ) {
    return false;
  }

  return !frames.some(isLipsBusinessFrame);
}
